/**
 * ActionPanelButton — a single icon button in the bottom toolbar.
 *
 * Icon-only in all modes (matching v1 ContextActionPanel pattern).
 * Tooltip shows label + optional shortcut on hover.
 * Disabled buttons are dimmed but remain visible for cognitive stability.
 */

import { memo, type ReactNode } from "react"
import { cn } from "@/shared/utils/general/cn"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/components/ui/tooltip"

interface ActionPanelButtonProps {
  /** Lucide icon element */
  icon: ReactNode
  /** Label text (tooltip) */
  label: string
  /** Click handler */
  onClick: () => void
  /** Whether the button is disabled (dimmed but visible) */
  disabled?: boolean
  /** Destructive action styling (red) */
  destructive?: boolean
  /** Optional keyboard shortcut hint */
  shortcut?: string
}

export const ActionPanelButton = memo(function ActionPanelButton({
  icon,
  label,
  onClick,
  disabled = false,
  destructive = false,
  shortcut,
}: ActionPanelButtonProps) {
  const tooltipText = shortcut ? `${label} (${shortcut})` : label

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <button
              type="button"
              onClick={disabled ? undefined : onClick}
              disabled={disabled}
              className={cn(
                "flex items-center justify-center rounded-lg p-2 transition-colors duration-150",
                "outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                "min-h-[40px] min-w-[40px]",
                // Normal state
                !destructive &&
                  !disabled &&
                  "text-foreground/70 hover:bg-primary/10 hover:text-foreground cursor-pointer",
                // Destructive state
                destructive &&
                  !disabled &&
                  "text-destructive/70 hover:bg-destructive/10 hover:text-destructive cursor-pointer",
                // Disabled state — dimmed but visible (v1 pattern: alpha 0.2)
                disabled && "text-foreground/20 cursor-default",
              )}
              aria-label={label}
            >
              <span className="[&_svg]:size-5">{icon}</span>
            </button>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">{tooltipText}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
})
